import { useMemo } from 'react';
import { Box, ButtonPrimaryTextOnly, ButtonSecondary, Typography } from '@creditas-ui/react';
import invariant from 'tiny-invariant';
import { FieldCheckbox, isFieldCheckbox } from '~/components/FieldCheckbox';
import { FieldHidden, isFieldHidden } from '~/components/FieldHidden';
import { FieldRadio, isFieldRadio } from '~/components/FieldRadio';
import { FieldSelect, isFieldSelect } from '~/components/FieldSelect';
import { FieldText, isFieldText } from '~/components/FieldText';
import { FieldTextarea, isFieldTextarea } from '~/components/FieldTextarea';
import { Stepper } from '~/components/Stepper';
import { useFormData } from '~/hooks/useFormData';
import { useSteps } from '~/hooks/useSteps';
import { ComponentType } from '~/shared/domain/models';
import type { JSX } from 'react';
import type { StepSchema, FieldsSchema } from '~/shared/domain/models';

export type StepProps = {
  data: StepSchema;
  stepIndex: number;
};

type Component = (props: StepProps) => JSX.Element;

const toFields = (field: FieldsSchema) => {
  const fieldKey = `${field.componentType}-${field.name}`;

  switch (field.componentType) {
    case ComponentType.FieldHidden:
      invariant(isFieldHidden(field));
      return <FieldHidden data={field} key={fieldKey} />;
    case ComponentType.FieldText:
      invariant(isFieldText(field));
      return <FieldText data={field} key={fieldKey} />;
    case ComponentType.FieldTextarea:
      invariant(isFieldTextarea(field));
      return <FieldTextarea data={field} key={fieldKey} />;
    case ComponentType.FieldSelect:
      invariant(isFieldSelect(field));
      return <FieldSelect data={field} key={fieldKey} />;
    case ComponentType.FieldRadio:
      invariant(isFieldRadio(field));
      return <FieldRadio data={field} key={fieldKey} />;
    case ComponentType.FieldCheckbox:
      invariant(isFieldCheckbox(field));
      return <FieldCheckbox data={field} key={fieldKey} />;
    default:
      return <pre key={fieldKey}>{JSON.stringify(field, null, 2)}</pre>;
  }
};

export const Step: Component = ({ data, stepIndex }) => {
  const { currentStep, hasPrev, hasNext, handlePrev, totalSteps } = useSteps();
  const { formError } = useFormData();
  const showStep = useMemo(() => stepIndex === currentStep, [currentStep, stepIndex]);

  return (
    <>
      {showStep && (
        <Box spaceY="16px">
          {totalSteps === 1 ? (
            <Typography color="primary.60" component="p" variant="bodyMdLight">
              {data.title}
            </Typography>
          ) : (
            <Stepper
              totalSteps={totalSteps}
              currentStep={currentStep}
              title={data.title}
              hasNext={hasNext}
            />
          )}
          <Typography color="primary.60" component="p" variant="bodyMdLight">
            {data.subtitle}
          </Typography>

          <Box spaceY="16px">{data.children.map(toFields)}</Box>

          {formError && (
            <Typography color="error.60" component="p" variant="bodyMdRegular">
              Tente novamente mais tarde.
            </Typography>
          )}

          <ButtonPrimaryTextOnly type="submit">
            {hasNext ? 'Proximo' : 'Enviar'}
          </ButtonPrimaryTextOnly>
          {hasPrev && (
            <ButtonSecondary type="button" onClick={handlePrev}>
              Voltar
            </ButtonSecondary>
          )}
        </Box>
      )}
    </>
  );
};
